import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus, Upload } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Selo, type CorSelo } from '../../componentes/base';
import { BotaoLixeira } from '../../componentes/lixeira';
import { Vazio } from '../../componentes/estados';
import { BotaoModeloCsv, colunaDoCsv, DialogoImportacao } from '../../componentes/importacao';
import { Rotulo } from '../../componentes/formulario';
import { Selecao } from '../../componentes/formulario';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { IconesFormas } from '../../componentes/formas-acesso';
import { getTodos } from '../../nucleo/api';
import { data, dinheiro, email, nomePessoa } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { FormFuncionario } from './FormFuncionario';
import { COR_CONTRATO, COR_ESTADO, CONTRATOS, ESTADOS, SITUACOES_ACESSO, cpfFormatado, telefoneFormatado, type Departamento, type Funcionario, type SituacaoAcesso } from './tipos';
import { ResponsavelCadastro } from './ResponsavelCadastro';

/** Val1 (28/10): cadastro "a complementar" (falta dado do RH ou item do onboarding). */
export function SeloACompletar({ f }: { f: Pick<Funcionario, 'a_completar'> }) {
  const { t } = useTranslation('pessoas');
  return f.a_completar?.length ? <Selo cor="laranja">{t('completar.selo')}</Selo> : null;
}

/** Acesso ao sistema: com acesso, convite enviado, convite expirado (val1), sem usuário ou sem e-mail. */
export function situacaoDoAcesso(f: Pick<Funcionario, 'usuario_id' | 'convite_pendente' | 'email' | 'acesso'>): SituacaoAcesso {
  if (f.acesso) return f.acesso;
  if (f.usuario_id) return 'com_acesso';
  if (f.convite_pendente) return 'convite_enviado';
  return f.email ? 'sem_usuario' : 'sem_email';
}

const COR_ACESSO: Record<SituacaoAcesso, CorSelo> = { com_acesso: 'verde', convite_enviado: 'laranja', convite_expirado: 'perigo', sem_usuario: 'neutro', sem_email: 'neutro' };

export function SeloAcesso({ f }: { f: Pick<Funcionario, 'usuario_id' | 'convite_pendente' | 'email' | 'acesso'> }) {
  const { t } = useTranslation('pessoas');
  const s = situacaoDoAcesso(f);
  return <Selo cor={COR_ACESSO[s]}>{t(`acesso.situacao.${s}`)}</Selo>;
}

export function Funcionarios() {
  const { t } = useTranslation('pessoas');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const navegar = useNavigate();
  const [novo, setNovo] = useState(false);
  const [estado, setEstado] = useState('');
  const [contrato, setContrato] = useState('');
  const [departamento, setDepartamento] = useState('');
  const [aCompletar, setACompletar] = useState('');
  // Val1 (28/10): filtro pela situação do acesso.
  const [acesso, setAcesso] = useState('');
  const [responsavel, setResponsavel] = useState(false);
  const [importar, setImportar] = useState(false);
  const [empresaPadrao, setEmpresaPadrao] = useState('');
  const empresas = s.empresas?.opcoes ?? [];
  // Lote de 19/10: a coluna Empresa (nome ou CNPJ) em qualquer posição; as linhas sem empresa vão para a escolhida aqui.
  const complementoImportacao = (csv: string) => {
    if (empresas.length < 2) return { conteudo: null };
    const col = colunaDoCsv(csv, ['empresa', 'cnpj', 'empresa do vinculo']);
    const exige = col.presente && col.vazias > 0;
    return {
      conteudo: (
        <Rotulo id="importar-empresa-padrao" rotulo={t('importar.empresaPadrao')} ajuda={t('importar.empresaPadraoAjuda')}>
          <Selecao
            id="importar-empresa-padrao"
            vazio={t('importar.empresaAtual')}
            opcoes={empresas.map((e) => ({ valor: e.id, rotulo: e.nome }))}
            value={empresaPadrao}
            onChange={(e) => setEmpresaPadrao(e.target.value)}
          />
        </Rotulo>
      ),
      opcoes: empresaPadrao ? { empresa_padrao: empresaPadrao } : undefined,
      bloqueio: exige && !empresaPadrao ? t('importar.empresaPadraoObrigatoria', { count: col.vazias }) : null,
    };
  };
  const departamentos = useQuery({ queryKey: ['pessoas', 'departamentos', 'select'], queryFn: () => getTodos<Departamento>('/pessoas/departamentos') });

  const colunas: Coluna<Funcionario>[] = [
    {
      chave: 'nome',
      rotulo: t('campos.nome'),
      ordenavel: true,
      principal: true,
      render: (f) => (
        <span className="flex min-w-0 flex-col">
          <span className="truncate font-medium" title={nomePessoa(f.nome)}>{nomePessoa(f.nome)}</span>
          {f.email && <span className="truncate text-xs text-tinta-3" title={email(f.email)}>{email(f.email)}</span>}
        </span>
      ),
    },
    { chave: 'funcao_nome', rotulo: t('campos.funcao') },
    { chave: 'departamento_nome', rotulo: t('campos.departamento') },
    { chave: 'contrato_tipo', rotulo: t('campos.contrato'), render: (f) => <Selo cor={COR_CONTRATO[f.contrato_tipo]}>{t(`contrato.${f.contrato_tipo}`)}</Selo> },
    { chave: 'admissao', rotulo: t('campos.admissao'), ordenavel: true, mono: true, render: (f) => data(f.admissao) },
    {
      chave: 'estado',
      rotulo: t('campos.estado'),
      render: (f) => (
        <span className="flex min-w-min flex-wrap gap-1">
          <Selo cor={COR_ESTADO[f.estado]}>{t(`estado.${f.estado}`)}</Selo>
          <SeloACompletar f={f} />
        </span>
      ),
    },
    {
      chave: 'acesso',
      rotulo: t('campos.acesso'),
      render: (f) => (
        <span className="flex min-w-min flex-wrap items-center gap-1.5">
          <SeloAcesso f={f} />
          {/* Val7 (29/10): os ícones das formas de acesso ativas do usuário ligado. */}
          {f.formas_acesso?.length ? <IconesFormas email={f.formas_acesso.includes('email')} usuario={f.formas_acesso.includes('usuario')} /> : null}
        </span>
      ),
    },
    { chave: 'gestor_nome', rotulo: t('campos.gestor'), oculta: true },
    { chave: 'calendario_nome', rotulo: t('campos.calendario'), oculta: true },
    // Lote de 21/10 (item 1): CPF e telefone são dados pessoais (só com `pessoas.confidencial`; sem ela, o servidor manda nulo).
    ...(s.pode('pessoas.confidencial')
      ? ([
          { chave: 'cpf', rotulo: t('campos.cpf'), mono: true, oculta: true, render: (f) => cpfFormatado(f.cpf) },
          { chave: 'telefone', rotulo: t('campos.telefone'), mono: true, oculta: true, render: (f) => telefoneFormatado(f.telefone) },
        ] as Coluna<Funcionario>[])
      : []),
    { chave: 'salario_centavos', rotulo: t('campos.salario'), alinhar: 'direita', mono: true, oculta: true, render: (f) => dinheiro(f.salario_centavos, f.moeda) },
  ];

  return (
    <>
      {(s.pode('pessoas.criar') || s.pode('pessoas.excluir') || s.pode('pessoas.editar') || s.pode('pessoas.importar')) && (
        <AcaoPrimaria>
          {s.pode('pessoas.excluir') && <BotaoLixeira entidades={['pessoas.funcionario']} />}
          {/* Lote de 19/10 (item 17): importador de funcionários (cria departamentos e funções; nunca convida). */}
          {/* Val5 (29/10): importar tem ação própria (`pessoas.importar`). */}
          {s.pode('pessoas.importar') && s.pode('pessoas.criar') && s.pode('pessoas.editar') && s.pode('pessoas.configurar') && (
            <>
              <BotaoModeloCsv caminho="/pessoas/funcionarios" />
              <Botao icone={<Upload className="size-4" />} onClick={() => setImportar(true)}>
                {t('importar.botao')}
              </Botao>
            </>
          )}
          {/* Val1 (28/10): quem recebe o aviso e o card do cadastro prévio. */}
          {s.pode('pessoas.editar') && <Botao onClick={() => setResponsavel(true)}>{t('responsavel.botao')}</Botao>}
          {s.pode('pessoas.criar') && (
            <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setNovo(true)}>
              {t('acoes.admitir')}
            </Botao>
          )}
        </AcaoPrimaria>
      )}
      <Tabela<Funcionario>
        tela="pessoas.funcionarios"
        caminho="/pessoas/funcionarios"
        params={{ estado: estado || undefined, contrato_tipo: contrato || undefined, departamento_id: departamento || undefined, a_completar: aCompletar || undefined, acesso: acesso || undefined }}
        filtros={{ estado, contrato_tipo: contrato, departamento_id: departamento, a_completar: aCompletar, acesso }}
        aoAplicarFiltros={(f) => {
          setEstado(String(f.estado));
          setContrato(String(f.contrato_tipo));
          setDepartamento(String(f.departamento_id));
          setACompletar(f.a_completar === 'true' || f.a_completar === 'false' ? f.a_completar : '');
          setAcesso(SITUACOES_ACESSO.includes(f.acesso as SituacaoAcesso) ? String(f.acesso) : '');
        }}
        colunas={colunas}
        entidade="pessoas.funcionario"
        aoClicar={(f) => navegar(`/pessoas/pessoas/funcionario/${f.id}`)}
        vazio={<Vazio texto={t('vazio.funcionarios')} />}
        filtrosExtra={
          <div className="flex flex-wrap gap-2">
            <div className="w-full sm:w-56">
              <Selecao aria-label={t('campos.estado')} vazio={t('filtros.ativosAfastados')} opcoes={[...ESTADOS.map((e) => ({ valor: e, rotulo: t(`estado.${e}`) })), { valor: 'todos', rotulo: tn('comum.todos') }]} value={estado} onChange={(e) => setEstado(e.target.value)} />
            </div>
            <div className="w-full sm:w-56">
              <Selecao aria-label={t('campos.contrato')} vazio={t('filtros.todosContratos')} opcoes={CONTRATOS.map((c) => ({ valor: c, rotulo: t(`contrato.${c}`) }))} value={contrato} onChange={(e) => setContrato(e.target.value)} />
            </div>
            <div className="w-full sm:w-56">
              <Selecao aria-label={t('campos.departamento')} vazio={t('filtros.todosDepartamentos')} opcoes={(departamentos.data?.itens ?? []).map((d) => ({ valor: d.id, rotulo: d.nome }))} value={departamento} onChange={(e) => setDepartamento(e.target.value)} />
            </div>
            <div className="w-full sm:w-56">
              <Selecao
                aria-label={t('completar.selo')}
                vazio={t('completar.filtro')}
                opcoes={[
                  { valor: 'true', rotulo: t('completar.so') },
                  { valor: 'false', rotulo: t('completar.completas') },
                ]}
                value={aCompletar}
                onChange={(e) => setACompletar(e.target.value)}
              />
            </div>
            <div className="w-full sm:w-56">
              <Selecao
                aria-label={t('campos.acesso')}
                vazio={t('acesso.filtro')}
                opcoes={SITUACOES_ACESSO.map((x) => ({ valor: x, rotulo: t(`acesso.situacao.${x}`) }))}
                value={acesso}
                onChange={(e) => setAcesso(e.target.value)}
              />
            </div>
          </div>
        }
      />
      <DialogoImportacao aberto={importar} aoFechar={() => setImportar(false)} caminho="/pessoas/funcionarios" titulo={t('importar.titulo')} aviso={t('importar.aviso')} complemento={complementoImportacao} />
      {responsavel && <ResponsavelCadastro aoFechar={() => setResponsavel(false)} />}
      <FormFuncionario aberto={novo} aoFechar={() => setNovo(false)} aoSalvar={(id) => navegar(`/pessoas/pessoas/funcionario/${id}`)} />
    </>
  );
}
